import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Search, Zap } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";

const fallbackCover =
  "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=1200&q=80";

function PublicNav() {
  return (
    <nav className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-6 lg:px-12 py-5">
      <Link to="/" className="flex items-center gap-2 text-white">
        <Logo to={null} dark iconSize={34} />
      </Link>
      <div className="flex items-center gap-5 text-sm">
        <Link
          to="/"
          className="text-slate-300 hover:text-white transition hidden sm:block"
        >
          Beranda
        </Link>
        <Link to="/blog" className="text-white font-semibold">
          Blog
        </Link>
        <Link
          to="/login"
          className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl font-semibold transition"
        >
          Masuk
        </Link>
      </div>
    </nav>
  );
}

export default function Blog() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const params = new URLSearchParams();
    if (cat) params.set("category", cat);
    if (search) params.set("search", search);
    params.set("per_page", "30");
    api
      .get(`/blog?${params}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ posts: [], categories: [] }));
  }, [cat, search]);

  const posts = data?.posts || [];
  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div
      className="bg-slate-950 min-h-screen text-white"
      data-testid="blog-page"
    >
      <Seo
        title="Blog RESADA — Tips Lead Generation, Sales Automation & WhatsApp Marketing"
        description="Artikel & panduan praktis lead generation, sales automation, WhatsApp/Email marketing, dan AI untuk tim sales di Indonesia."
        path="/blog"
      />
      {/* Cinematic hero */}
      <header className="relative overflow-hidden">
        <PublicNav />
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-sky-950" />
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="absolute -top-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-sky-500/20 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12 pt-32 pb-16">
          <p className="text-sky-400 font-semibold tracking-widest uppercase text-xs mb-4">
            RESADA Insights
          </p>
          <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[1.05] max-w-3xl">
            Strategi <span className="text-sky-400">revenue</span> & otomasi
            penjualan yang benar-benar bekerja.
          </h1>
          <p className="text-slate-300 mt-6 max-w-xl text-lg">
            Taktik praktis lead generation, sales automation, WhatsApp & email
            marketing, dan AI untuk tim sales Indonesia.
          </p>
          <div className="mt-8 relative max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-4 top-3.5" />
            <input
              data-testid="blog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setSearch(q)}
              placeholder="Cari artikel..."
              className="w-full bg-white/10 border border-white/15 rounded-xl pl-10 pr-4 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:border-sky-400"
            />
          </div>
        </div>
      </header>

      {/* Category filter */}
      <div className="sticky top-0 z-30 bg-slate-950/90 backdrop-blur border-y border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-3 flex gap-2 overflow-x-auto">
          <button
            onClick={() => setCat("")}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${!cat ? "bg-sky-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}
          >
            Semua
          </button>
          {(data?.categories || []).map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`blog-cat-${c}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${cat === c ? "bg-sky-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        {!data ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-80 rounded-2xl bg-white/5 animate-pulse"
              />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <p className="text-slate-400 text-center py-20">
            Belum ada artikel. Cek lagi nanti ya.
          </p>
        ) : (
          <>
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                data-testid="blog-featured"
                className="group grid lg:grid-cols-2 gap-8 mb-14 rounded-3xl overflow-hidden bg-white/5 hover:bg-white/[0.08] transition border border-white/5"
              >
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <img
                    src={featured.cover_url || fallbackCover}
                    alt={featured.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <span className="text-sky-400 text-xs font-bold uppercase tracking-wide">
                    {featured.category}
                  </span>
                  <h2 className="font-display text-3xl font-extrabold mt-3 leading-tight group-hover:text-sky-300 transition">
                    {featured.title}
                  </h2>
                  <p className="text-slate-300 mt-4">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 mt-6 text-sm text-slate-400">
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-4 h-4" /> {featured.read_time} mnt
                      baca
                    </span>
                    <span className="text-sky-400 font-semibold inline-flex items-center gap-1">
                      Baca artikel{" "}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid="blog-card"
                  className="group rounded-2xl overflow-hidden bg-white/5 hover:bg-white/[0.08] border border-white/5 transition flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={p.cover_url || fallbackCover}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <span className="absolute top-3 left-3 bg-slate-900/80 backdrop-blur text-sky-300 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full">
                      {p.category}
                    </span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-lg leading-snug group-hover:text-sky-300 transition">
                      {p.title}
                    </h3>
                    <p className="text-slate-400 text-sm mt-2 line-clamp-3 flex-1">
                      {p.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 mt-4">
                      <Clock className="w-3.5 h-3.5" /> {p.read_time} mnt baca
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
